import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, CircularProgress,
  InputAdornment, Chip, Avatar, Tooltip, useTheme, Divider
} from '@mui/material';
import { Add, Edit, Delete, Search, Business, Email, Phone, Person, Public } from '@mui/icons-material';
import { Customer, CustomerContact } from '../../types';
import {
  getCustomers, createCustomer, updateCustomer, deleteCustomer,
  getContacts, addContact, updateContact, deleteContact,
} from '../../api/customers';

const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';
const BORDER_COLOR = '#e8eaf0';

const AVATAR_PALETTE = ['#6366f1','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#ef4444'];
const avatarColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const emptyCustomer: Partial<Customer> = { companyName: '', address: '', country: '', phone: '', contactPerson: '', email: '' };
const emptyContact: Partial<CustomerContact> = { name: '', title: '', email: '', phone: '' };

const actionBtn = (border: string) => ({
  width: 30, height: 30, borderRadius: '6px',
  border: `1px solid ${border}`, color: '#64748b', bgcolor: '#fff',
});

const CustomersPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');

  // Müşteri dialog
  const [open, setOpen]       = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm]       = useState<Partial<Customer>>(emptyCustomer);
  const [saving, setSaving]   = useState(false);

  // Contacts (sadece edit modunda)
  const [contacts, setContacts]               = useState<CustomerContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact]   = useState<CustomerContact | null>(null);
  const [contactForm, setContactForm]         = useState<Partial<CustomerContact>>(emptyContact);
  const [contactSaving, setContactSaving]     = useState(false);

  const load = async () => {
    try { setLoading(true); setCustomers(await getCustomers()); }
    catch { setError('Müşteriler yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => customers.filter(c =>
      !search ||
      c.companyName?.toLowerCase().includes(search.toLowerCase()) ||
      c.country?.toLowerCase().includes(search.toLowerCase()) ||
      c.contactPerson?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
  ), [customers, search]);

  // ── Müşteri ──────────────────────────────────────────────────────────────
  const handleOpen = async (customer?: Customer) => {
    setEditing(customer || null);
    setForm(customer || emptyCustomer);
    setShowContactForm(false);
    setContacts([]);
    setError('');
    setOpen(true);
    if (customer) {
      setContactsLoading(true);
      try { setContacts(await getContacts(customer.id)); }
      catch { /* sessiz hata */ }
      finally { setContactsLoading(false); }
    }
  };

  const handleSave = async () => {
    if (!form.companyName) return;
    setSaving(true);
    try {
      if (editing) {
        await updateCustomer(editing.id, form);
        setOpen(false);
        load();
      } else {
        const created = await createCustomer(form);
        load();
        // Switch to edit mode so user can add contacts immediately
        setEditing(created);
        setContactsLoading(true);
        try { setContacts(await getContacts(created.id)); }
        catch { /* sessiz hata */ }
        finally { setContactsLoading(false); }
      }
    } catch { setError('Kayıt sırasında hata oluştu'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try { await deleteCustomer(id); load(); }
    catch { setError('Silme sırasında hata oluştu'); }
  };

  // ── Contacts ─────────────────────────────────────────────────────────────
  const openContactForm = (contact?: CustomerContact) => {
    setEditingContact(contact || null);
    setContactForm(contact ? { name: contact.name, title: contact.title, email: contact.email, phone: contact.phone } : emptyContact);
    setShowContactForm(true);
  };

  const handleSaveContact = async () => {
    if (!editing || !contactForm.name) return;
    setContactSaving(true);
    try {
      if (editingContact) await updateContact(editingContact.id, contactForm);
      else await addContact(editing.id, contactForm);
      setShowContactForm(false);
      setContacts(await getContacts(editing.id));
    } catch { setError('Kayıt sırasında hata oluştu'); }
    finally { setContactSaving(false); }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!editing || !window.confirm('Kişiyi silmek istediğinize emin misiniz?')) return;
    try {
      await deleteContact(contactId);
      setContacts(await getContacts(editing.id));
    } catch { setError('Silme sırasında hata oluştu'); }
  };

  const uniqueCountries = customers.map(c => c.country).filter((v, i, a) => v && a.indexOf(v) === i).length;

  return (
      <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>Müşteriler</Typography>
            <Box sx={{ display: 'flex', gap: 2.5, mt: 0.75 }}>
              <Typography sx={{ fontSize: '13px', color: TEXT2 }}>
                <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{filtered.length}</Box> müşteri
                {filtered.length !== customers.length && <Box component="span"> / {customers.length} toplam</Box>}
              </Typography>
              {uniqueCountries > 0 && (
                  <Typography sx={{ fontSize: '13px', color: TEXT2 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{uniqueCountries}</Box> ülke
                  </Typography>
              )}
            </Box>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}
                  sx={{ bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px', px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38, '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
            Yeni Müşteri
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ── Arama ── */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
          <TextField placeholder="Firma, ülke, yetkili veya e-posta ara..." value={search}
              onChange={e => setSearch(e.target.value)} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: TEXT2 }} /></InputAdornment>, sx: { borderRadius: '8px', fontSize: '13px', minWidth: 320 } }}
              sx={{ '& fieldset': { borderColor: BORDER } }} />
        </Paper>

        {/* ── Tablo ── */}
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>
        ) : (
            <TableContainer component={Paper} elevation={0}
                            sx={{ borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden', bgcolor: SURFACE }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Firma', 'Yetkili', 'Ülke', 'İletişim', 'İşlem'].map(h => (
                        <TableCell key={h} align={h === 'İşlem' ? 'center' : 'left'}
                                   style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12, fontSize: 11, fontWeight: 600, color: '#8a92a6', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                          {h}
                        </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 8, color: TEXT2 }}>
                          <Business sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                          <Typography sx={{ mb: 1, fontSize: '14px' }}>{search ? 'Aramayla eşleşen müşteri bulunamadı' : 'Henüz müşteri eklenmemiş'}</Typography>
                          {!search && <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => handleOpen()} sx={{ borderRadius: '8px', textTransform: 'none', borderColor: BORDER }}>İlk müşteriyi ekle</Button>}
                        </TableCell>
                      </TableRow>
                  ) : filtered.map(c => (
                      <TableRow key={c.id} sx={{ '& td': { borderBottom: `1px solid ${BORDER_COLOR}`, py: 0 }, '&:hover td': { bgcolor: '#fafbfe !important' } }}>
                        <TableCell sx={{ height: 60 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: avatarColor(c.companyName), fontSize: '12px', fontWeight: 700 }}>{getInitials(c.companyName)}</Avatar>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.palette.text.primary }}>{c.companyName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {c.contactPerson
                              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><Person sx={{ fontSize: 13, color: TEXT2 }} /><Typography sx={{ fontSize: '12px' }}>{c.contactPerson}</Typography></Box>
                              : <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Typography>}
                        </TableCell>
                        <TableCell>
                          {c.country
                              ? <Chip icon={<Public sx={{ fontSize: 12, color: '#64748b !important' }} />} label={c.country} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, borderRadius: '6px', bgcolor: '#f1f5f9', color: '#475569', '& .MuiChip-icon': { ml: 0.5, mr: -0.4 } }} />
                              : <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                            {c.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><Email sx={{ fontSize: 12, color: TEXT2 }} /><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{c.email}</Typography></Box>}
                            {c.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><Phone sx={{ fontSize: 12, color: TEXT2 }} /><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{c.phone}</Typography></Box>}
                            {!c.email && !c.phone && <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>—</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ pr: 2 }}>
                          <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => handleOpen(c)} style={actionBtn(BORDER)}
                                          sx={{ '&:hover': { background: '#fff8ed !important', color: '#d97706 !important', borderColor: '#fed7aa !important' } }}>
                                <Edit sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" onClick={() => handleDelete(c.id)} style={actionBtn(BORDER)}
                                          sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        {/* ── Müşteri / Düzenle Dialog ── */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ bgcolor: BRAND, color: '#fff', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" />
              {editing ? editing.companyName : 'Yeni Müşteri'}
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

              {/* Firma bilgileri */}
              <TextField label="Firma Adı *" value={form.companyName || ''} onChange={e => setForm({ ...form, companyName: e.target.value })}
                         fullWidth autoFocus size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                         InputProps={{ startAdornment: <InputAdornment position="start"><Business sx={{ fontSize: 16, color: '#8a92a6' }} /></InputAdornment> }} />
              <TextField label="Ülke" value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })}
                         fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                         InputProps={{ startAdornment: <InputAdornment position="start"><Public sx={{ fontSize: 16, color: '#8a92a6' }} /></InputAdornment> }} />
              <TextField label="Adres" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })}
                         fullWidth multiline rows={2} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
            </Box>

            {/* ── İlgili Kişiler ── */}
            <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#8a92a6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      İlgili Kişiler
                    </Typography>
                    {editing && (
                      <Button size="small" startIcon={<Add sx={{ fontSize: 13 }} />}
                              onClick={() => openContactForm()}
                              sx={{ fontSize: '12px', textTransform: 'none', color: BRAND, borderRadius: '6px', py: 0.3, '&:hover': { bgcolor: '#f0f4ff' } }}>
                        Kişi Ekle
                      </Button>
                    )}
                  </Box>

                  {!editing ? (
                      <Typography sx={{ fontSize: '12px', color: 'text.disabled', py: 1 }}>Müşteriyi kaydettikten sonra kişi ekleyebilirsiniz.</Typography>
                  ) : contactsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} sx={{ color: BRAND }} /></Box>
                  ) : (
                      <>
                        {contacts.length === 0 && !showContactForm && (
                            <Typography sx={{ fontSize: '12px', color: 'text.disabled', py: 1 }}>Henüz ilgili kişi eklenmemiş.</Typography>
                        )}

                        {contacts.map((ct, idx) => (
                            <Box key={ct.id}>
                              {idx > 0 && <Divider sx={{ my: 1 }} />}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                                <Avatar sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: avatarColor(ct.name), fontSize: '11px', fontWeight: 700 }}>{getInitials(ct.name)}</Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{ct.name}</Typography>
                                    {ct.title && <Chip label={ct.title} size="small" sx={{ height: 18, fontSize: '10px', borderRadius: '5px', bgcolor: '#f1f5f9', color: '#475569' }} />}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2 }}>
                                    {ct.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}><Email sx={{ fontSize: 11, color: TEXT2 }} /><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{ct.email}</Typography></Box>}
                                    {ct.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}><Phone sx={{ fontSize: 11, color: TEXT2 }} /><Typography sx={{ fontSize: '11px', color: TEXT2 }}>{ct.phone}</Typography></Box>}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Düzenle">
                                    <IconButton size="small" onClick={() => openContactForm(ct)} style={actionBtn(BORDER_COLOR)}
                                                sx={{ '&:hover': { background: '#fff8ed !important', color: '#d97706 !important', borderColor: '#fed7aa !important' } }}>
                                      <Edit sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Sil">
                                    <IconButton size="small" onClick={() => handleDeleteContact(ct.id)} style={actionBtn(BORDER_COLOR)}
                                                sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                                      <Delete sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>
                        ))}

                        {/* Kişi ekleme / düzenleme formu */}
                        {showContactForm && (
                            <Box sx={{ mt: 1.5, p: 2, bgcolor: '#f8fbff', borderRadius: '8px', border: `1px solid ${BORDER_COLOR}` }}>
                              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: BRAND, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {editingContact ? 'Kişiyi Düzenle' : 'Yeni Kişi'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                <TextField label="Ad Soyad *" value={contactForm.name || ''} onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                                           fullWidth size="small" autoFocus sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                                <TextField label="Ünvan" value={contactForm.title || ''} onChange={e => setContactForm({ ...contactForm, title: e.target.value })}
                                           fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                <TextField label="E-posta" value={contactForm.email || ''} onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                                           fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                           InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 14, color: '#8a92a6' }} /></InputAdornment> }} />
                                <TextField label="Telefon" value={contactForm.phone || ''} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                                           fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                           InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 14, color: '#8a92a6' }} /></InputAdornment> }} />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button onClick={() => setShowContactForm(false)} size="small" sx={{ borderRadius: '7px', color: '#8a92a6', textTransform: 'none' }}>İptal</Button>
                                <Button variant="contained" onClick={handleSaveContact} disabled={contactSaving || !contactForm.name} size="small"
                                        sx={{ bgcolor: BRAND, borderRadius: '7px', textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
                                  {contactSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Kaydet'}
                                </Button>
                              </Box>
                            </Box>
                        )}
                      </>
                  )}
                </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={() => setOpen(false)} sx={{ borderRadius: '8px', color: '#8a92a6', textTransform: 'none' }}>Kapat</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || !form.companyName}
                    sx={{ bgcolor: BRAND, borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none', '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kaydet'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default CustomersPage;
